
import device from '@@/bits/device'
import { isAppUsing, isDebugMode } from '@@/bits/flip'
import { PiniaMapAction, PiniaMapState } from '@@/bits/pinia'
import { observeSizeChange } from '@@/bits/resize_observer'
import { Component, Getter, State, Vue, defineAsyncComponent, namespace } from '@@/bits/vue'
import { initializeZoomSdk } from '@@/bits/zoom'
import { useCommandsStore } from '@@/pinia/commands'
import { useSurfaceGuidedTemplateStore } from '@@/pinia/surface_guided_template_store'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import type { Wall } from '@@/types'
import SurfaceOffline from '@@/vuecomponents/surface_offline.vue'
import { useFullscreen } from '@@/vuecomposables/fullscreen'
import PageStylingMixin from '@@/vuemixins/page_styling_mixin'
import PresenceMixin from '@@/vuemixins/presence_mixin'
import { processedUrl } from '@padlet/vivaldi-client'
import tinykeys from 'tinykeys'
const mixins: any[] = [PageStylingMixin, PresenceMixin]

@Component({
  components: {
    // Don't lazy load SurfaceOffline. Otherwise, if the internet disconnects, we need to show this component,
    // but we can't show it because we can't fetch it.
    SurfaceOffline,
    SurfaceContainer: defineAsyncComponent(() => import('@@/vuecomponents/surface_container.vue')),
    AppGlobalSnackbar: defineAsyncComponent(() => import('@@/vuecomponents/app_global_snackbar.vue')),
    GlobalSnackbarVuex: defineAsyncComponent(() => import('@@/vuecomponents/GlobalSnackbarVuex.vue')),
    GlobalSnackbar: defineAsyncComponent(() => import('@@/vuecomponents/GlobalSnackbar.vue')),
    GlobalMentionAutocomplete: defineAsyncComponent(() => import('@@/vuecomponents/GlobalMentionAutocomplete.vue')),
    SurfaceDragAndDropOverlay: defineAsyncComponent(() => import('@@/vuecomponents/surface-drag-and-drop-overlay.vue')),
    SurfaceSlideshow: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSlideshow.vue')),
    CommandDialog: defineAsyncComponent(() => import('@@/vuecomponents/CommandDialog.vue')),
    SurfaceExportsDocumentStatus: defineAsyncComponent(
      () => import('@@/vuecomponents/SurfaceExportsDocumentStatus.vue'),
    ),
    SurfaceOnboardingPanel: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceOnboardingPanel.vue')),
    SurfaceOnboardingGalleryPanelMobile: defineAsyncComponent(
      () => import('@@/vuecomponents/SurfaceOnboardingGalleryPanelMobile.vue'),
    ),
    SurfaceOnboardingGuidedPanel: defineAsyncComponent(
      () => import('@@/vuecomponents/SurfaceOnboardingGuidedPanel.vue'),
    ),
  },
  mixins,
})
export default class SurfaceApp extends Vue {
  $refs!: {
    mobileOnboardingPanel: Vue & { $el: HTMLElement }
  }

  isAppUsing = isAppUsing

  @State isOnline!: boolean
  @Getter isPostable!: boolean
  @Getter isSlideshow!: string
  @Getter isGeneratingPDF!: boolean
  @Getter wallId!: string
  @Getter wall!: Wall

  @namespace('snackbars').Getter xSnackbar!: boolean

  @PiniaMapState(useCommandsStore) xCommandDialog!: string
  @PiniaMapAction(useCommandsStore) showCommandDialog!: () => void
  @PiniaMapState(useSurfaceOnboardingPanelStore) showOnboardingPanel!: boolean
  @PiniaMapState(useSurfaceOnboardingPanelStore) isGalleryTemplatePanel!: boolean
  @PiniaMapState(useSurfaceOnboardingPanelStore) xGalleryOnboardingPanelMobile!: boolean
  @PiniaMapState(useSurfaceOnboardingPanelStore) xGalleryOnboardingPanelDesktop!: boolean
  @PiniaMapState(useSurfaceOnboardingPanelStore) xBlankOnboardingPanel!: boolean
  @PiniaMapState(useSurfaceGuidedTemplateStore) xOnboardingGuidedPanel!: boolean

  removeKeyboardShortcuts = () => {}
  async configureZoomApp() {
    const zoomSdk = (await import('@zoom/appssdk')).default
    await initializeZoomSdk()
    return zoomSdk
  }

  created() {
    useFullscreen()
  }

  beforeMount() {
    if (isAppUsing('fullClientSurface')) {
      this.updateTitle()
      this.initializeFavicon()
      this.insertPrintNotificationElement()
      this.addThemeColorMetaTag()
      const mobileAppVersion = Number(document.body.getAttribute('ww-variant-version'))
      if (!this.isSlideshow && !this.isGeneratingPDF && !(device.app && mobileAppVersion >= 191)) {
        this.updatePageBackground()
      }
      this.updateBodyFontAttribute()
      this.updateBodyColorSchemeAttribute()
    }
  }

  updateTitle(): void {
    const wallTitle = this.wall?.title ?? this.__('Padlet') // Fallback
    if (this.isSlideshow) {
      document.title = `Slideshow | ${wallTitle}`
      return
    }
    if (this.isGeneratingPDF) {
      document.title = this.__('Generating PDF...')
      return
    }
    document.title = wallTitle
  }

  updatePageBackground(): void {
    const bodyTag = document.body
    const wallBackground = this.wall.background
    bodyTag.style.backgroundSize = wallBackground.fill === 'cover' ? 'cover' : 'auto'
    const effect = wallBackground.effect === 'blur' ? 'blur:10' : undefined
    const imageUrl = processedUrl(wallBackground.url, {
      width: wallBackground.width,
      height: wallBackground.height,
      effect,
    })
    bodyTag.style.backgroundImage = `url('${imageUrl}')`
  }

  addThemeColorMetaTag(): void {
    const metaTag = document.createElement('meta')
    metaTag.setAttribute('name', 'theme-color')
    metaTag.setAttribute('content', this.wall.background.dominant_color_as_rgb)
    const headerTag = document.head
    headerTag.appendChild(metaTag)
  }

  initializeFavicon(): void {
    const headerTag = document.head
    if (this.isSlideshow) {
      const iconLinkTag = document.createElement('link')
      iconLinkTag.setAttribute('rel', 'icon')
      iconLinkTag.setAttribute('href', `/slideshow/favicon.ico`)
      iconLinkTag.setAttribute('sizes', '32x32')
      headerTag.appendChild(iconLinkTag)
      const appleIconLinkTag = document.createElement('link')
      appleIconLinkTag.setAttribute('rel', 'apple-touch-icon')
      appleIconLinkTag.setAttribute('href', `/slideshow/apple-touch-icon.png`)
      appleIconLinkTag.setAttribute('sizes', '180x180')
      headerTag.appendChild(appleIconLinkTag)
      const manifestLinkTag = document.createElement('link')
      manifestLinkTag.setAttribute('rel', 'manifest')
      manifestLinkTag.setAttribute('href', `/slideshow/site.webmanifest`)
      headerTag.appendChild(manifestLinkTag)
      return
    }
    if (this.wall.portrait !== undefined && this.wall.portrait !== '') {
      const iconLinkTag = document.createElement('link')
      iconLinkTag.setAttribute('rel', 'icon')
      const iconUrl = processedUrl(this.wall.portrait, {
        width: 64,
        height: 64,
        preset: 'icon',
      })
      iconLinkTag.setAttribute('href', iconUrl)
      headerTag.appendChild(iconLinkTag)
      const appleIconLinkTag = document.createElement('link')
      appleIconLinkTag.setAttribute('rel', 'apple-touch-icon')
      const appleIconUrl = processedUrl(this.wall.portrait, {
        width: 180,
        height: 180,
        preset: 'icon',
      })
      appleIconLinkTag.setAttribute('href', appleIconUrl)
      appleIconLinkTag.setAttribute('sizes', '180x180')
      headerTag.appendChild(appleIconLinkTag)
      return
    }
    const iconLinkTag = document.createElement('link')
    iconLinkTag.setAttribute('rel', 'icon')
    iconLinkTag.setAttribute('href', 'https://padlet.net/favicon.ico')
    iconLinkTag.setAttribute('sizes', '32x32')
    headerTag.appendChild(iconLinkTag)
    const iconSvgLinkTag = document.createElement('link')
    iconSvgLinkTag.setAttribute('rel', 'icon')
    iconSvgLinkTag.setAttribute('type', 'image/svg+xml')
    iconSvgLinkTag.setAttribute('href', 'https://padlet.net/favicon.svg')
    headerTag.appendChild(iconSvgLinkTag)
    const appleIconLinkTag = document.createElement('link')
    appleIconLinkTag.setAttribute('rel', 'apple-touch-icon')
    appleIconLinkTag.setAttribute('href', 'https://padlet.net/apple-touch-icon.png')
    appleIconLinkTag.setAttribute('sizes', '180x180')
    headerTag.appendChild(appleIconLinkTag)
  }

  insertPrintNotificationElement(): void {
    const printNotificationElement = document.createElement('div')
    const appElement = document.getElementById('app')
    if (appElement === null) return
    printNotificationElement.setAttribute('id', 'print-notification')
    printNotificationElement.setAttribute(
      'title',
      this.__('To print this page properly, open the "Share" menu on the top right of the page and select "Print".'),
    )
    document.body.insertBefore(printNotificationElement, appElement)
  }

  updateBodyFontAttribute(): void {
    const bodyTag = document.body
    if (this.wall.font_id === undefined) return
    bodyTag.setAttribute('data-font', this.wall.font_id.toString())
  }

  updateBodyColorSchemeAttribute(): void {
    const bodyTag = document.body
    if (this.wall.color_scheme === undefined) return
    bodyTag.setAttribute('data-color-scheme', this.wall.color_scheme.toString())
  }

  mobileOnboardingPanelHeight = '0px'
  async mounted() {
    if (device.zoomApp) {
      const zoomSdk = await this.configureZoomApp()
      zoomSdk.expandApp({ action: 'expand' })
    }
    if (isDebugMode || isAppUsing('showCommandPalette')) {
      this.removeKeyboardShortcuts = tinykeys(document.documentElement as HTMLElement, {
        '$mod+KeyK': this.showCommandDialog,
      })
    }
  }

  handleOnboardingPanelMounted(): void {
    if (this.$refs.mobileOnboardingPanel?.$el === undefined) return
    this.mobileOnboardingPanelHeight = `${this.$refs.mobileOnboardingPanel.$el.clientHeight}px`
    observeSizeChange(this.$refs.mobileOnboardingPanel.$el, () => {
      if (this.$refs.mobileOnboardingPanel?.$el === undefined) return
      this.mobileOnboardingPanelHeight = `${this.$refs.mobileOnboardingPanel.$el.clientHeight}px`
    })
  }

  beforeDestroy(): void {
    if (isDebugMode || isAppUsing('showCommandPalette')) {
      this.removeKeyboardShortcuts()
    }
  }
}
